import { MouseEvent, useCallback } from 'react'
import cn from 'classnames'
import tableCellStyles from '../../TableCell/TableCell.module.scss'
import styles from './CellNoteIndicator.module.scss'

interface IProps {
  noteCount: number
  /**
   * The cell carries suggestions awaiting a decision. Reinforces that on the count bubble,
   * which is what replaces the dog-ear on hover — the clickable accent itself is drawn by
   * CellAttributionMarker, which needs its own hit target.
   *
   * Pending specifically, not attribution generally: established facts are authoritative and
   * view-only (assertReviewable rejects them), so calling them "review pending" would promise
   * an action the API refuses. Their discoverability comes from the accent, which renders for
   * any attribution and names them correctly in its own tooltip.
   */
  hasPendingReview?: boolean
  /**
   * The cell carries a note grounded in an amendment rather than written by a person. Colours
   * the dog-ear instead of adding a mark: it is the same kind of thing — a real note you open
   * and read — by a different author, and the corner is already the place that says "note here".
   */
  isEstablished?: boolean
  className?: string
  onClick?: () => void
}

const CellNoteIndicator = ({
  noteCount,
  hasPendingReview,
  isEstablished,
  className,
  onClick,
}: IProps) => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      onClick?.()
    },
    [onClick],
  )

  if (noteCount === 0) return null

  return (
    <div
      data-snapshot-hide
      className={cn(styles.root, className, { [styles.clickable]: !!onClick })}
      title={
        hasPendingReview
          ? `${noteCount} note${noteCount > 1 ? 's' : ''} · review pending`
          : isEstablished
            ? `${noteCount} note${noteCount > 1 ? 's' : ''} · from an amendment`
            : `${noteCount} note${noteCount > 1 ? 's' : ''}`
      }
      onClick={handleClick}
    >
      <div
        className={cn(tableCellStyles.triangle, {
          [tableCellStyles.triangleEstablished]: isEstablished,
        })}
      />

      <div
        className={cn(tableCellStyles.circle, {
          [tableCellStyles.circleReview]: hasPendingReview,
        })}
      >
        <span className={tableCellStyles.count}>{noteCount}</span>
      </div>
    </div>
  )
}

export default CellNoteIndicator
